import { useAnalytics } from '../../../../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../../../../helpers/analytics/trackEvent';
import { getPageUrl, getUserUrl } from '../../../../../../../helpers/urls';
import { Button } from '../../../../../../Clickables/Buttons/Button';
import { CollapsibleText } from '../../../../../../CollapsibleText/CollapsibleText';
import { TagContent } from '../../../../../../TagContent/TagContent';
import { PostViewOptions } from '../../../../../constants/constants';
import { getCatchGearItems } from '../../../../../helpers/getCatchGearItems';
import type { Post } from '../../../../../types/Post';
import { CommentCTA } from '../../../../CommentCTA/CommentCTA';
import { PostActions } from '../../../../PostActions/PostActions';
import { PostDetailsSection } from '../../../../PostDetailsSection/PostDetailsSection';
import { PostGearCards } from '../../../../PostGearCards/PostGearCards';
import { PostMediaSection } from '../../../../PostMediaSection/PostMediaSection';
import { UserStatement } from '../../../../UserStatement/UserStatement';

import { usePostItem } from './usePostItem';

export interface PostItemProps {
  className?: string;
  onViewChange: (viewName: PostViewOptions, viewPostId?: string) => void;
  post: Post;
}

export const PostItem = ({ className, onViewChange: handleViewChange, post }: PostItemProps) => {
  const { currentPage } = useAnalytics();
  const postItem = usePostItem(post);
  const { userId, userDetails, displayEntity, userAvatarUrl, statementDate, postId, hasCatch } =
    postItem;
  const { content, images, video, isPostLiked, usedGearsCount, userNickName, usedGearFromPost } =
    postItem;
  const { commentsCount, postLikesCount, postUrl } = postItem;
  const hasImages = images.length > 0;
  const hasVideo = video?.screenshot;
  const isPostWithoutImagesAndVideo = !hasCatch && !hasImages && !hasVideo;
  const catchDetailsButtonText = 'Catch details';
  const seeAllCommentsButtonText = 'See comments';

  const showDisplayEntity = displayEntity?.displayName && displayEntity.displayIcon;
  const displayUserId = showDisplayEntity && displayEntity._id ? displayEntity._id : userId;
  const displayUserNickname =
    showDisplayEntity && displayEntity.displayName ? displayEntity.displayName : userNickName;
  const displayUserAvatarUrl =
    showDisplayEntity && displayEntity.displayIcon.url
      ? displayEntity.displayIcon.url
      : userAvatarUrl;
  const displayUserUrl = showDisplayEntity
    ? getPageUrl(displayUserId, displayUserNickname)
    : getUserUrl(userNickName);

  const handleCommentsClick = (): void => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'read-more',
        source: 'post-viewer-comments',
        page: currentPage,
      },
    });
    handleViewChange('postComments', postId);
  };

  const handleCommentDownloadClick = (): void => {
    handleViewChange('registerWallView');
  };

  const handleDetailsClick = (): void => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'read-more',
        source: 'post-viewer-details',
        page: currentPage,
      },
    });
    handleViewChange('postDetails', postId);
  };
  const handleGearButtonClick = (): void => {
    handleViewChange('postDetailsWithScroll', postId);
  };

  return (
    <article className={className} id={postId} key={postId}>
      <UserStatement
        statementDate={statementDate}
        userAvatarUrl={displayUserAvatarUrl}
        userDetails={userDetails}
        userNickName={displayUserNickname}
        userUrl={displayUserUrl}
      >
        <CollapsibleText maxLinesNumber={4}>
          <TagContent id={displayUserId} text={content} url="/tags/" />
        </CollapsibleText>
      </UserStatement>
      {!isPostWithoutImagesAndVideo && (
        <PostMediaSection
          images={images}
          video={video ?? undefined}
          onGearButtonClick={handleGearButtonClick}
          usedGearsCount={usedGearsCount}
          userNickname={displayUserNickname}
          isTouchDevice={true}
          hasCatch={hasCatch}
        />
      )}
      {!hasCatch && usedGearFromPost && usedGearFromPost.length > 0 && (
        <PostDetailsSection className="m-2" id="catch-products" title={'Gear used'}>
          <PostGearCards postGear={getCatchGearItems(usedGearFromPost)} />
        </PostDetailsSection>
      )}
      <PostActions
        isPostLiked={isPostLiked}
        onLikesInfoViewChange={() => {
          handleViewChange('postLikes');
        }}
        onNotLoggedIn={() => {
          handleViewChange('postList');
        }}
        postId={postId}
        postLikesCount={postLikesCount}
        postType={hasCatch ? 'catch' : 'post'}
        postURL={postUrl}
      >
        {hasCatch && (
          <Button onClick={handleDetailsClick} size="sm" variant="buttonSecondary">
            {catchDetailsButtonText}
          </Button>
        )}

        {commentsCount > 0 ? (
          <Button
            className="text-midnight! mt-2 font-semibold"
            onClick={handleCommentsClick}
            variant="textNone"
          >
            {seeAllCommentsButtonText}
          </Button>
        ) : (
          <CommentCTA onDownloadAppClick={handleCommentDownloadClick} className="mt-2" />
        )}
      </PostActions>
    </article>
  );
};
